import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  FiBell,
  FiCalendar,
  FiEye,
  FiFile,
  FiFileText,
  FiMail,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";
import { MessageDetailsModal } from "./MessageDetailsModal";
import { ReminderPanel } from "./ReminderPanel";
import { getCurrentDate, getCurrentMonthFirstDate } from "../../../Utilitie";
import { FaRegMessage } from "react-icons/fa6";
import SendWhatsappMsg from "../../../Extra/SendWhatsappMsg";
import Loading from "../../../Extra/Loading";

const urlPrefix = process.env.REACT_APP_URL_PREFIX;
const COLORS = {
  blue: "#063b87",
  blueLight: "#0a57bb",
  green: "#087344",
  background: "#f3f6fb",
  text: "#243247",
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDayDifference = (value) => {
  if (!value) return null;

  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return Math.round((target - today) / 86400000);
};

const getReminderStatus = (value) => {
  const difference = getDayDifference(value);

  if (difference === null) return null;
  if (difference < 0) {
    return {
      label: `Overdue by ${Math.abs(difference)} day(s)`,
      color: "#dc3545",
      priority: 0,
    };
  }
  if (difference === 0) {
    return {
      label: "Due today",
      color: "#dc3545",
      priority: 1,
    };
  }
  if (difference <= 3) {
    return {
      label: `Due in ${difference} day(s)`,
      color: "#b77900",
      priority: 2,
    };
  }

  return null;
};

const getTypeColor = (type) =>
  type === "ICMS" ? "#0a57bb" : type === "FOIS" ? "#087344" : "#64748b";

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

function KpiCard({ title, value, subtitle, icon, accent, onClick }) {
  return (
    <div className="col-12 col-sm-6 col-xl-3">
      <button
        type="button"
        className="card border-0 shadow-sm h-100 w-100 text-start"
        onClick={onClick}
        style={{
          borderRadius: 14,
          borderLeft: `4px solid ${accent}`,
          cursor: onClick ? "pointer" : "default",
          transition: "transform .2s ease",
        }}
      >
        <div className="card-body p-3 p-md-4">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div className="small text-secondary fw-semibold mb-2">
                {title}
              </div>
              <div className="fs-3 fw-bold" style={{ color: COLORS.blue }}>
                {value}
              </div>
              <div className="small text-secondary mt-1">{subtitle}</div>
            </div>

            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 44,
                height: 44,
                background: `${accent}14`,
                color: accent,
                fontSize: 21,
              }}
            >
              {icon}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}

function TypeChart({ counts, total, onSelect }) {
  const types = [
    { name: "ICMS", color: "#0a57bb" },
    { name: "FOIS", color: "#087344" },
    { name: "Other", color: "#7c6acb" },
  ];

  return (
    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
      <div className="card-body p-3 p-md-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h6 className="fw-bold mb-1">Message distribution</h6>
            <div className="small text-secondary">Messages by type</div>
          </div>
          <FiMail size={22} color={COLORS.blue} />
        </div>

        {types.map((item) => {
          const count = counts[item.name] || 0;
          const percentage = total ? (count / total) * 100 : 0;

          return (
            <button
              type="button"
              key={item.name}
              className="w-100 border-0 bg-transparent text-start mb-4 p-0"
              onClick={() =>
                onSelect(item.name === "Other" ? "ALL" : item.name)
              }
              title={`Show ${item.name} messages`}
            >
              <div className="d-flex justify-content-between mb-2">
                <span className="small fw-semibold">{item.name}</span>
                <span className="small text-secondary">
                  {count} ({percentage.toFixed(1)}%)
                </span>
              </div>

              <div
                className="progress"
                style={{ height: 9, background: "#e9eef5", borderRadius: 20 }}
              >
                <div
                  className="progress-bar"
                  role="progressbar"
                  aria-label={`${item.name} messages`}
                  aria-valuenow={percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  style={{
                    width: `${percentage}%`,
                    background: item.color,
                    borderRadius: 20,
                    transition: "width .3s ease",
                  }}
                />
              </div>
            </button>
          );
        })}

        <div className="small text-secondary">
          Select a type to filter the message list.
        </div>
      </div>
    </div>
  );
}

export default function MessageDashboard({
  detailUrl = `${urlPrefix}msg/get_messages`,
  getDocumentUrl,
  apiClient = axios,
}) {
  const [date, setDate] = useState({
    from: getCurrentMonthFirstDate(),
    to: getCurrentDate(),
  });
  const listUrl = `${urlPrefix}msg/get_messages?startDate=${date.from}&endDate=${date.to}`;
  const [messages, setMessages] = useState([]);
  const [activeType, setActiveType] = useState("ALL");
  const [search, setSearch] = useState("");
  const [reminderFilter, setReminderFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const loadMessages = useCallback(async () => {
    if (!listUrl) {
      setError("Please configure the message list API URL.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiClient.get(listUrl);
      const payload = response.data;

      if (payload?.status && payload.status !== "ok") {
        throw new Error(payload.message || "Unable to fetch messages.");
      }

      setMessages(Array.isArray(payload?.data) ? payload.data : []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [apiClient, listUrl]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const counts = useMemo(() => {
    const result = { ICMS: 0, FOIS: 0, Other: 0 };

    messages.forEach((item) => {
      if (item.type === "ICMS") result.ICMS += 1;
      else if (item.type === "FOIS") result.FOIS += 1;
      else result.Other += 1;
    });

    return result;
  }, [messages]);

  const allReminders = useMemo(
    () =>
      messages
        .map((item) => ({
          ...item,
          reminderStatus: getReminderStatus(item.reminder_date),
        }))
        .filter((item) => item.reminderStatus)
        .sort((a, b) => {
          const difference =
            a.reminderStatus.priority - b.reminderStatus.priority;

          if (difference !== 0) return difference;

          return new Date(a.reminder_date) - new Date(b.reminder_date);
        }),
    [messages],
  );

  const filteredMessages = useMemo(() => {
    const query = search.trim().toLowerCase();

    return messages.filter((item) => {
      const matchesType = activeType === "ALL" || item.type === activeType;

      const matchesSearch =
        !query ||
        [
          item.subject,
          item.msg_sender,
          item.type,
          item.head,
          String(item.id),
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        );

      const status = getReminderStatus(item.reminder_date);

      const matchesReminder =
        reminderFilter === "ALL" ||
        (reminderFilter === "HAS_REMINDER" && Boolean(item.reminder_date)) ||
        (reminderFilter === "OVERDUE" && status?.label.startsWith("Overdue")) ||
        (reminderFilter === "TODAY" && status?.label === "Due today");

      return matchesType && matchesSearch && matchesReminder;
    });
  }, [messages, activeType, search, reminderFilter]);

  const visibleCounts = useMemo(() => {
    const result = { ICMS: 0, FOIS: 0, Other: 0 };

    filteredMessages.forEach((item) => {
      if (item.type === "ICMS") result.ICMS += 1;
      else if (item.type === "FOIS") result.FOIS += 1;
      else result.Other += 1;
    });

    return result;
  }, [filteredMessages]);

  const visibleReminders = useMemo(
    () =>
      allReminders.filter((item) => {
        return (
          (activeType === "ALL" || item.type === activeType) &&
          (!search.trim() ||
            [item.subject, item.msg_sender, item.type, String(item.id)].some(
              (value) =>
                String(value ?? "")
                  .toLowerCase()
                  .includes(search.trim().toLowerCase()),
            ))
        );
      }),
    [allReminders, activeType, search],
  );

  const openDetails = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailError("");

    if (!detailUrl) {
      setDetailError("Please configure the message detail API URL.");
      return;
    }

    setDetailLoading(true);

    try {
      const url =
        typeof detailUrl === "function" ? detailUrl(id) : `${detailUrl}/${id}`;

      const response = await apiClient.get(url);
      const payload = response.data;

      if (payload?.status && payload.status !== "ok") {
        throw new Error(payload.message || "Unable to fetch message details.");
      }

      const items = Array.isArray(payload?.data) ? payload.data : [];
      const found = items.find((item) => String(item.id) === String(id));

      if (!found && items.length === 0) {
        throw new Error("No details were returned for this message.");
      }

      setDetail(found || items[0]);
    } catch (err) {
      setDetailError(getErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError("");
  };

  const selectReminderFilter = (filter) => {
    setReminderFilter(filter);
    setActiveType("ALL");
  };

  return (
    <div
      className="container-fluid px-3 px-md-4 py-4"
      style={{
        background: COLORS.background,
        minHeight: "100vh",
        color: COLORS.text,
      }}
    >
      {loading && <Loading />}
      <style>{`
        .rs-message-dashboard .rs-message-row:hover {
          background: #f5f8ff;
        }
        .rs-message-dashboard .rs-filter-button {
          border: 1px solid #dce5f1;
          background: white;
          color: #44536a;
          border-radius: 8px;
          padding: 8px 15px;
          font-size: 14px;
          font-weight: 600;
        }
        .rs-message-dashboard .rs-filter-button.active {
          color: white;
          background: #063b87;
          border-color: #063b87;
        }
        .rs-message-dashboard .message-html-content img {
          max-width: 100%;
          height: auto;
        }
        .rs-message-dashboard .message-html-content table {
          max-width: 100%;
          border-collapse: collapse;
        }
        .rs-message-dashboard .message-html-content td,
        .rs-message-dashboard .message-html-content th {
          overflow-wrap: anywhere;
        }
        .rs-message-dashboard .message-html-content a {
          overflow-wrap: anywhere;
        }
        .rs-message-dashboard .message-html-content p:last-child {
          margin-bottom: 0;
        }
      `}</style>

      <div className="rs-message-dashboard">
        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <h3 className="fw-bold mb-1" style={{ color: COLORS.blue }}>
              <FaRegMessage /> Message Management
            </h3>
            <p className="text-secondary mb-0">
              Manage ICMS and FOIS messages, attachments, and reminders.
            </p>
          </div>

          <div className="d-flex">
            <input
              className="form-control me-1"
              type="date"
              value={date.from}
              onChange={(e) => setDate({ ...date, from: e.target.value })}
            />
            <input
              className="form-control me-1"
              type="date"
              max={getCurrentDate()}
              value={date.to}
              onChange={(e) => setDate({ ...date, to: e.target.value })}
            />
            <button
              className="btn btn-primary d-flex align-items-center gap-2 me-1"
              style={{
                background: COLORS.blue,
                borderColor: COLORS.blue,
                borderRadius: 9,
              }}
              onClick={loadMessages}
              disabled={loading}
            >
              <FiRefreshCw className={loading ? "spin" : ""} />
              {loading ? "Fetching..." : "Fetch"}
            </button>
          </div>
        </div>

        {error && (
          <div
            className="alert alert-danger d-flex justify-content-between align-items-center gap-3"
            role="alert"
          >
            <span>{error}</span>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={loadMessages}
            >
              Retry
            </button>
          </div>
        )}

        {/* KPI cards */}
        <div className="row g-3 mb-4">
          <KpiCard
            title="Total Messages"
            value={messages.length}
            subtitle="All available messages"
            icon={<FiMail />}
            accent={COLORS.blue}
            onClick={() => {
              setActiveType("ALL");
              setReminderFilter("ALL");
            }}
          />

          <KpiCard
            title="ICMS Messages"
            value={counts.ICMS}
            subtitle="ICMS communication"
            icon={<FiFileText />}
            accent="#0a57bb"
            onClick={() => {
              setActiveType("ICMS");
              setReminderFilter("ALL");
            }}
          />

          <KpiCard
            title="FOIS Messages"
            value={counts.FOIS}
            subtitle="FOIS communication"
            icon={<FiMail />}
            accent={COLORS.green}
            onClick={() => {
              setActiveType("FOIS");
              setReminderFilter("ALL");
            }}
          />

          <KpiCard
            title="Action Required"
            value={allReminders.length}
            subtitle="Overdue or due within 3 days"
            icon={<FiBell />}
            accent="#dc3545"
            onClick={() => selectReminderFilter("OVERDUE")}
          />
        </div>

        {/* Dashboard panels */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-xl-5">
            <TypeChart
              counts={visibleCounts}
              total={filteredMessages.length}
              onSelect={(type) => {
                setActiveType(type);
                setReminderFilter("ALL");
              }}
            />
          </div>

          <div className="col-12 col-xl-7">
            <ReminderPanel
              reminders={visibleReminders}
              onView={openDetails}
              loadMessages={loadMessages}
            />
          </div>
        </div>

        {/* Message list */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
          <div className="card-body p-3 p-md-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
              <div>
                <h5 className="fw-bold mb-1" style={{ color: COLORS.blue }}>
                  Message Register
                </h5>
                <div className="small text-secondary">
                  Showing {filteredMessages.length} of {messages.length}{" "}
                  messages
                </div>
              </div>

              <div
                className="position-relative"
                style={{ minWidth: 230, maxWidth: 360, flex: "1 1 260px" }}
              >
                <FiSearch
                  className="position-absolute top-50 translate-middle-y text-secondary"
                  style={{ left: 12 }}
                />
                <input
                  className="form-control ps-5"
                  placeholder="Search subject, sender, or ID..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  aria-label="Search messages"
                />
              </div>
            </div>

            {/* Type filters */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              {[
                ["ALL", "All messages"],
                ["ICMS", "ICMS"],
                ["FOIS", "FOIS"],
              ].map(([type, label]) => (
                <button
                  type="button"
                  key={type}
                  className={`rs-filter-button ${activeType === type ? "active" : ""}`}
                  onClick={() => setActiveType(type)}
                >
                  {label}
                  <span className="ms-2 opacity-75">
                    {type === "ALL" ? messages.length : counts[type] || 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Reminder filters */}
            <div className="row g-2 mb-4">
              <div className="col-12 col-sm-5 col-md-4 col-lg-3">
                <label className="form-label small fw-semibold mb-1">
                  Reminder status
                </label>
                <select
                  className="form-select"
                  value={reminderFilter}
                  onChange={(event) => setReminderFilter(event.target.value)}
                >
                  <option value="ALL">All messages</option>
                  <option value="HAS_REMINDER">Has reminder date</option>
                  <option value="OVERDUE">Overdue reminders</option>
                  <option value="TODAY">Due today</option>
                </select>
              </div>
            </div>

            {loading && messages.length === 0 ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="small text-secondary mt-3 mb-0">
                  Loading messages...
                </p>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="text-center py-5">
                <FiMail size={38} color="#9aa8bc" />
                <h6 className="fw-bold mt-3">No messages found</h6>
                <p className="text-secondary small mb-0">
                  Try changing the type, reminder filter, or search text.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr
                      className="small text-secondary"
                      style={{ background: "#f6f8fc" }}
                    >
                      <th>Sr</th>
                      <th className="py-3">Message</th>
                      <th className="py-3">Type</th>
                      <th className="py-3">Date</th>
                      <th className="py-3">Reminder</th>
                      <th className="py-3 text-center">Files</th>
                      <th colSpan={2} className="py-3 ">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredMessages.map((item, i) => {
                      const status = getReminderStatus(item.reminder_date);

                      return (
                        <tr className="rs-message-row" key={item.id}>
                          <td>{i + 1}</td>
                          <td style={{ minWidth: 240 }}>
                            <div className="fw-semibold mb-1">
                              {item.subject || "Untitled message"}
                            </div>
                            <div className="small text-secondary">
                              {item.msg_sender || "Unknown sender"}
                            </div>
                            <div className="small text-secondary">
                              ID: {item.id}
                            </div>
                          </td>

                          <td>
                            <span
                              className="badge rounded-pill px-3 py-2"
                              style={{
                                color: getTypeColor(item.type),
                                background: `${getTypeColor(item.type)}14`,
                              }}
                            >
                              {item.type || "Other"}
                            </span>
                          </td>

                          <td className="small text-nowrap">
                            <FiCalendar className="me-1 text-secondary" />
                            {formatDate(item.date)}
                          </td>

                          <td style={{ minWidth: 150 }}>
                            <div className="small">
                              {formatDate(item.reminder_date)}
                            </div>
                            {status && (
                              <div
                                className="small fw-semibold mt-1"
                                style={{ color: status.color }}
                              >
                                {status.label}
                              </div>
                            )}
                          </td>

                          <td className="text-center">
                            {item.document_count > 0 ? (
                              <span className="badge text-bg-light border">
                                <FiFile className="me-1" />
                                {item.document_count}
                              </span>
                            ) : (
                              <span className="text-secondary small">—</span>
                            )}
                          </td>

                          <td className="">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary d-inline-flex align-items-center "
                              style={{
                                background: COLORS.blue,
                                borderColor: COLORS.blue,
                                borderRadius: 8,
                              }}
                              onClick={() => openDetails(item.id)}
                            >
                              <FiEye />
                              View
                            </button>
                          </td>
                          <td className="text-nowrap">
                            <SendWhatsappMsg
                              defaultNumber=""
                              defaultMessage={item?.message}
                              defaultAttachments={[]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="text-secondary small text-center mt-4">
          RailSetu Message Management · ICMS / FOIS
        </div>

        {selectedId !== null && (
          <MessageDetailsModal
            message={detail}
            loading={detailLoading}
            error={detailError}
            onClose={closeDetails}
          />
        )}
      </div>
    </div>
  );
}
